import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useMemo, useState } from "react"
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form"

import { DESTINATION_DEFAULT_FORM_VALUES } from "../../constants"
import { useAdjustmentLotValidation, useCreateAddPalletsAdjustment } from "../../hooks"
import { type AddPalletsAdjustmentFormValues, addPalletsAdjustmentFormSchema } from "../../schemas"
import type { AdjustmentLotReference } from "../../types"
import {
  filterAddPalletsAdjustmentDuplicateLotIndexes,
  getAdjustmentLotDefaultFormValues,
  toAddPalletsAdjustmentCreateInput,
} from "../../utils"
import { AdjustmentTypeHeader } from "../AdjustmentTypeHeader"
import { AdjustmentUntestedLotsConfirmationDialog } from "../AdjustmentUntestedLotsConfirmationDialog"
import { QuantityAdjustmentFormDetails } from "../Quantity/QuantityAdjustmentFormDetails"

import { AddPalletsAdjustmentFormDestinations } from "./AddPalletsAdjustmentFormDestinations"
import { AddPalletsAdjustmentFormPallets } from "./AddPalletsAdjustmentFormPallets"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { InventoryAdjustmentType, type InventoryByProductWarehouse, type Product } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface AddPalletsAdjustmentFormDialogProps {
  product: Product
  warehouseAreaId: string
  warehouseInventories: InventoryByProductWarehouse[]
  warehouseOptions: Option<string>[]
  onClose: () => void
  onSuccess: () => void
}

export const AddPalletsAdjustmentFormDialog: FC<AddPalletsAdjustmentFormDialogProps> = ({
  product,
  warehouseAreaId,
  warehouseInventories,
  warehouseOptions,
  onClose,
  onSuccess,
}) => {
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)
  const [untestedLots, setUntestedLots] = useState<AdjustmentLotReference[]>([])

  const { createAdjustment } = useCreateAddPalletsAdjustment()
  const { validateLotsTested } = useAdjustmentLotValidation()

  const uomName = product.packageUsWeightUomDetails?.name ?? ""

  const form = useForm<AddPalletsAdjustmentFormValues>({
    resolver: zodResolver(addPalletsAdjustmentFormSchema),
    defaultValues: {
      warehouseAreaId,
      palletRow: {
        quantityPerPallet: product.palletUsWeight,
        palletCount: 1,
        lots: [getAdjustmentLotDefaultFormValues(product)],
      },
      destinations: [DESTINATION_DEFAULT_FORM_VALUES],
    },
    mode: "onChange",
  })

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isValid, errors, isDirty, isSubmitting },
  } = form
  const selectedWarehouseAreaId = watch("warehouseAreaId")

  const selectedWarehouseArea = useMemo(
    () =>
      warehouseInventories.find((inventory) => inventory.warehouseArea?.warehouseAreaId === selectedWarehouseAreaId)
        ?.warehouseArea,
    [warehouseInventories, selectedWarehouseAreaId]
  )

  const {
    fields: lotFields,
    append: appendLot,
    remove: removeLot,
  } = useFieldArray({
    control,
    name: "palletRow.lots",
  })

  const {
    fields: destinationFields,
    append: appendDestination,
    remove: removeDestination,
  } = useFieldArray({
    control,
    name: "destinations",
  })

  const lots = useWatch({ control, name: "palletRow.lots" })
  const duplicateLotIndexes = useMemo(() => filterAddPalletsAdjustmentDuplicateLotIndexes(lots), [lots])

  const destinations = useWatch({ control, name: "destinations" })
  const selectedShelvingIds = useMemo(
    () =>
      destinations
        .map((destination) => destination.shelving.warehouseShelvingId)
        .filter((warehouseShelvingId) => warehouseShelvingId !== null),
    [destinations]
  )

  const canSubmit = isValid && !isSubmitting && !errors.document && !duplicateLotIndexes.size

  const handlePalletCountChange = (value: number | null) => {
    if (!value) return

    const destinationCount = form.getValues("destinations")?.length ?? 0

    if (value > destinationCount) {
      const destinationsToAdd = Array.from({ length: value - destinationCount }, () => DESTINATION_DEFAULT_FORM_VALUES)
      appendDestination(destinationsToAdd)
    } else if (value < destinationCount) {
      const destinationsToRemove = Array.from({ length: destinationCount - value }, (_, i) => destinationCount - i - 1)
      removeDestination(destinationsToRemove)
    }
  }

  const handleLotAdd = () => {
    appendLot(getAdjustmentLotDefaultFormValues(product))
  }

  const handleWarehouseChange = () => {
    setValue(
      "destinations",
      Array.from({ length: destinationFields.length }, () => DESTINATION_DEFAULT_FORM_VALUES),
      { shouldValidate: true }
    )
  }

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  const submitForm = async (formValues: AddPalletsAdjustmentFormValues) => {
    const isSuccess = await createAdjustment(
      toAddPalletsAdjustmentCreateInput(formValues, product.productId),
      formValues.comment,
      formValues.document
    )

    if (isSuccess) onSuccess()
  }

  const handleValidateAndSubmit = async (formValues: AddPalletsAdjustmentFormValues) => {
    const untestedBlockingLots = await validateLotsTested(formValues.palletRow.lots, product)

    if (untestedBlockingLots.length > 0) {
      setUntestedLots(untestedBlockingLots)
      return
    }

    submitForm(formValues)
  }

  const handleUntestedLotsConfirm = () => {
    setUntestedLots([])
    submitForm(form.getValues())
  }

  if (isCancelConfirmationOpen) {
    return <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
  }

  if (untestedLots.length > 0) {
    return (
      <AdjustmentUntestedLotsConfirmationDialog
        untestedLots={untestedLots}
        onClose={() => setUntestedLots([])}
        onConfirm={handleUntestedLotsConfirm}
      />
    )
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth={false}
      PaperProps={{ className: "h-full" }}
      onClose={handleCancel}
      data-testid='inventory.add-pallets-adjustment-form-dialog'
    >
      <DialogHeader title='Inventory Adjustment & Pallet Operations' onClose={handleCancel} />
      <FormProvider {...form}>
        <AdjustmentTypeHeader
          adjustmentType={InventoryAdjustmentType.AddNewPallets}
          product={product}
          warehouseOptions={warehouseOptions}
          onWarehouseChange={handleWarehouseChange}
        />
        <DialogContent className='grid flex-1 grid-cols-[1fr_368px] gap-x-6 overflow-hidden px-10 pb-10 pt-6'>
          <Box className='flex min-h-0 min-w-0 flex-col gap-y-6 overflow-hidden'>
            <AddPalletsAdjustmentFormPallets
              product={product}
              uomName={uomName}
              lotFields={lotFields}
              duplicateLotIndexes={duplicateLotIndexes}
              onPalletCountChange={handlePalletCountChange}
              onLotAdd={handleLotAdd}
              onLotRemove={removeLot}
            />
            <AddPalletsAdjustmentFormDestinations
              warehouseArea={selectedWarehouseArea}
              destinationFields={destinationFields}
              selectedShelvingIds={selectedShelvingIds}
            />
          </Box>
          <QuantityAdjustmentFormDetails />
        </DialogContent>
      </FormProvider>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button
          appearance='outlined'
          onClick={handleCancel}
          data-testid='inventory.add-pallets-adjustment-form-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={!canSubmit}
          onClick={handleSubmit(handleValidateAndSubmit)}
          data-testid='inventory.add-pallets-adjustment-form-dialog.submit-button'
        >
          Confirm Adjustment
        </Button>
      </DialogActions>
    </Dialog>
  )
}
